import PropTypes from "prop-types";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Components
import MetricInput from "./MetricInput";

// MUI
import { Grid, Typography } from "@mui/material";

const useRequiredValues = (metrics = []) => Array.from(metrics).map((_, key) => ({ value: key + 1 }));

const useSelectedRequiredValues = (metrics = []) => (metrics?.filter((element) => Boolean(element.required))?.length || 0) + 1;

const removeInvalidValues = (values = [], requiredValues = 0, currentNumber = null) => {
	if (!values.length) {
		return [];
	}
	const results = values.filter((element) => element.value >= requiredValues);
	if (currentNumber) {
		const isCurrentIncluded = results.some((item) => item.value === currentNumber);
		if (!isCurrentIncluded) {
			return [...results, { value: currentNumber }];
		}
	}
	return results;
};

const getInputDefault = () => {
	return [
		{
			type: "Percent",
			min: -100,
			max: 100,
			decimalsEditable: true,
			decimals: 2,
		},
		{
			type: "Dollar",
			min: -100000,
			max: 100000,
			decimalsEditable: false,
			decimals: 2,
		},
	];
};

export default function MetricTierItem(props) {
	const {
		metricItem = null,
		showTitles = false,
		labels = {
			required: "Required",
			above: "Above",
			below: "Below",
		},
		errors = {
			missed: false,
			attained: false,
			numberRequired: false,
		},
		aboveIdentifier = "attained",
		belowIdentifier = "missed",
		belowDisabled = false,
		aboveDisabled = false,
		metrics = [],
		dataInputAbove = [...getInputDefault()],
		dataInputBelow = [...getInputDefault()],
		loading = false,
		readOnly = false,
		handleUpdateMetric = () => {},
	} = props;

	const requiredValues = useRequiredValues(metrics);
	const numberSelectedValues = useSelectedRequiredValues(metrics);

	const numberRequired = metricItem?.["numberRequired"];

	const onHandleUpdateInput = (area, attribute, value) => {
		const metric = { ...metricItem };
		if (attribute === "input") {
			metric[area][attribute] = value;
			metric[area]["value"] = 0;
		} else {
			metric[area]["value"] = value ? Number(value) : 0;
		}

		handleUpdateMetric?.(metric, {
			area,
			isUpdatingInput: attribute === "input",
		});
	};

	const onHandleChangeRequired = (selected) => {
		const { value } = selected || {};
		const metric = {
			...metricItem,
			numberRequired: value || 1,
		};
		handleUpdateMetric?.(metric);
	};

	return (
		<Grid container justifyContent={"center"} mt={2} columnSpacing={2} sx={{ maxWidth: "465px !important" }}>
			<Grid item>
				<Grid container direction="column" justifyContent={"center"} alignItems={"center"}>
					{showTitles && (
						<Grid item>
							<Typography className="draw-label" mr={0.2}>
								{labels.required}
							</Typography>
						</Grid>
					)}
					<Grid item>
						<SmartSelector
							selectDefault
							selectBy={"value"}
							showBy={"value"}
							options={removeInvalidValues(requiredValues, numberSelectedValues, numberRequired) || []}
							selected={numberRequired ? { value: numberRequired } : null}
							inputProps={{
								placeholder: "Required",
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							disabled={readOnly}
							error={errors?.numberRequired}
							loading={loading}
							sx={{ minWidth: "70px", width: "70px" }}
							onChangeAction={onHandleChangeRequired}
						/>
					</Grid>
				</Grid>
			</Grid>

			<Grid item>
				<MetricInput
					title={labels?.below || "Below"}
					showTitle={showTitles}
					inputData={dataInputBelow}
					inputDefinition={metricItem?.[belowIdentifier]}
					error={errors?.[belowIdentifier]}
					readOnly={readOnly || loading || belowDisabled}
					onActionUpdate={(value) => onHandleUpdateInput(belowIdentifier, "value", value)}
					onChangeType={(_, input) => onHandleUpdateInput(belowIdentifier, "input", input)}
				/>
			</Grid>

			<Grid item>
				<MetricInput
					title={labels?.above || "Above"}
					showTitle={showTitles}
					inputData={dataInputAbove}
					inputDefinition={metricItem?.[aboveIdentifier]}
					error={errors?.[aboveIdentifier]}
					readOnly={readOnly || loading || aboveDisabled}
					onActionUpdate={(value) => onHandleUpdateInput(aboveIdentifier, "value", value)}
					onChangeType={(_, input) => onHandleUpdateInput(aboveIdentifier, "input", input)}
				/>
			</Grid>
		</Grid>
	);
}
MetricTierItem.propTypes = {
	metricItem: PropTypes.object,
	showTitles: PropTypes.bool,
	labels: PropTypes.shape({
		required: PropTypes.string,
		above: PropTypes.string,
		below: PropTypes.string,
	}),
	errors: PropTypes.shape({
		missed: PropTypes.bool,
		attained: PropTypes.bool,
		numberRequired: PropTypes.bool,
	}),
	aboveIdentifier: PropTypes.string,
	belowIdentifier: PropTypes.string,
	belowDisabled: PropTypes.bool,
	aboveDisabled: PropTypes.bool,
	metrics: PropTypes.array,
	dataInputAbove: PropTypes.array,
	dataInputBelow: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	handleUpdateMetric: PropTypes.func,
};
